import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, useOutletContext, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  DialogActions,
  Alert,
  Switch,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  Divider,
  Chip,
  Tooltip,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import BusinessIcon from '@mui/icons-material/Business';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import CalculateIcon from '@mui/icons-material/Calculate';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BarcodeScannerComponent from 'react-qr-barcode-scanner';

import {
  useCreatePurchaseOrderMutation,
  useUpdatePurchaseOrderMutation,
  useGetPaymentAccountsQuery,
  useLazyGetSuppliersQuery,
  useGetPurchaseOrderQuery,
  useUpdatePurchaseOrderStatusMutation,
  useDeletePurchaseOrderMutation,
  useGetPurchaseOrderNoteQuery,
} from '../../../store/api/purchaseApi';
import { useLazyGetProductsQuery } from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import ProductFormContent from '../../products/pages/ProductFormContent';
import { SupplierFormContent } from './SupplierForm';
import { formatItemDisplayName } from '../../sales/utils/formatItemDisplayName';
import { getLocalDateString } from '../../../shared/utils/dateUtils';

function PurchaseOrderForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const supplierIdFromUrl = searchParams.get('supplierId') || '';
  const fromPoId = searchParams.get('fromPoId') || '';
  const isLpoFromUrl = searchParams.get('lpo') === '1';
  const isEdit = !!id && (location.pathname.endsWith('/edit') || location.search.includes('edit=true'));
  const isView = !!id && !isEdit;

  const outletContext = useOutletContext();
  const setNavigationBlocked = outletContext?.setNavigationBlocked;

  const [createPurchaseOrder, { isLoading: isSubmittingCreate }] = useCreatePurchaseOrderMutation();
  const [updatePurchaseOrder, { isLoading: isSubmittingUpdate }] = useUpdatePurchaseOrderMutation();
  const isSubmitting = isSubmittingCreate || isSubmittingUpdate;
  const [updatePurchaseOrderStatus] = useUpdatePurchaseOrderStatusMutation();
  const [deletePurchaseOrder] = useDeletePurchaseOrderMutation();

  const [productDialog, setProductDialog] = useState({ open: false, productId: null });
  const [triggerSupplierSearch, { data: supplierSearchData, isFetching: searchingSuppliers }] = useLazyGetSuppliersQuery();
  const [supplierSearchOptions, setSupplierSearchOptions] = useState([]);
  const { data: paymentAccounts } = useGetPaymentAccountsQuery();
  const [triggerProductSearch, { data: productSearchData, isFetching: searchingProducts }] = useLazyGetProductsQuery();
  const { data: company } = useGetCurrentCompanyQuery();

  const itemNameFormat = company?.printItemNameFormat || 'name_sku';
  const currency = company?.currency || 'USD';
  const showWholesalePrice = company?.showWholesalePrice !== false;
  const dec = typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2;

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: dec,
        maximumFractionDigits: dec,
      }).format(amount);
    } catch {
      return `${currency} ${amount.toFixed(dec)}`;
    }
  };

  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const { data: existingOrder, isLoading: loadingOrder } = useGetPurchaseOrderQuery(id, {
    skip: !id,
  });
  const { data: poNoteData } = useGetPurchaseOrderNoteQuery(fromPoId, {
    skip: !fromPoId || isView || isEdit,
  });

  const [formData, setFormData] = useState({
    invoiceNumber: '',
    lpoNumber: '',
    invoiceDate: getLocalDateString(),
    purchaseType: 'cash',
    postingType: 'inventory',
    supplierId: '',
    orderDate: getLocalDateString(),
    notes: '',
    discountTotal: 0,
    roundOff: 0,
    paidAmount: 0,
    paymentMethod: 'cash',
    paymentReferenceNo: '',
    paymentAccountId: '',
    isTaxable: true,
    purchaseOrderNoteId: '',
  });

  const [payments, setPayments] = useState([
    { method: 'cash', amount: '', paymentAccountId: '', referenceNo: '' },
  ]);
  const [autoPaymentAmount, setAutoPaymentAmount] = useState(true);

  const [items, setItems] = useState([]);
  const [focusQtyIndex, setFocusQtyIndex] = useState(null);
  const qtyInputRefs = useRef({});
  const productSearchInputRef = useRef(null);
  const [productSearch, setProductSearch] = useState('');
  const [scanOpen, setScanOpen] = useState(false);
  const [scanError, setScanError] = useState('');
  const [pendingBarcode, setPendingBarcode] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [duplicateDialog, setDuplicateDialog] = useState({
    open: false,
    existingIndex: -1,
    product: null,
  });
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [imeiModal, setImeiModal] = useState({ open: false, itemIndex: -1, imeis: [], text: '' });
  const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false);
  const [barcodeSelection, setBarcodeSelection] = useState([]);

  useEffect(() => {
    if (poNoteData && !isView) {
      setFormData((prev) => ({
        ...prev,
        supplierId: poNoteData.supplierId || prev.supplierId,
        lpoNumber: poNoteData.poNumber || prev.lpoNumber,
        notes: poNoteData.notes || prev.notes,
        discountTotal: Number(poNoteData.discountTotal) || 0,
        roundOff: Number(poNoteData.roundOff) || 0,
        isTaxable: poNoteData.isTaxable !== false,
        purchaseOrderNoteId: poNoteData.id,
      }));

      if (poNoteData.supplier) {
        setSupplierSearchOptions((prev) => {
          if (!prev.find((s) => s.id === poNoteData.supplier.id)) {
            return [poNoteData.supplier, ...prev];
          }
          return prev;
        });
      }

      if (poNoteData.items && poNoteData.items.length > 0) {
        setItems(
          poNoteData.items.map((it) => ({
            productId: it.productId,
            name: it.product?.name || '',
            sku: it.product?.sku || '',
            brand: it.product?.brand || '',
            unit: it.unit || it.product?.unit || 'pcs',
            quantity: Number(it.quantity) || 1,
            unitPrice: Number(it.unitPrice) || 0,
            sellingPrice:
              it.sellingPrice != null
                ? Number(it.sellingPrice)
                : it.product?.sellingPrice
                ? Number(it.product.sellingPrice)
                : '',
            wholesalePrice:
              it.wholesalePrice != null
                ? Number(it.wholesalePrice)
                : it.product?.wholesalePrice
                ? Number(it.product.wholesalePrice)
                : '',
            requiresImei: it.product?.requiresImei || false,
            requiresDualImei: it.product?.requiresDualImei || false,
            imeis: [],
            isReturn: false,
            isDamaged: false,
            damageReason: '',
          }))
        );
      }
    }
  }, [poNoteData, isView]);

  useEffect(() => {
    if (supplierSearchData?.data) {
      setSupplierSearchOptions(supplierSearchData.data);
    } else if (Array.isArray(supplierSearchData)) {
      setSupplierSearchOptions(supplierSearchData);
    }
  }, [supplierSearchData]);

  useEffect(() => {
    if (existingOrder?.supplier && !supplierSearchOptions.find((s) => s.id === existingOrder.supplier.id)) {
      setSupplierSearchOptions((prev) => [existingOrder.supplier, ...prev]);
    }
  }, [existingOrder, supplierSearchOptions]);

  const selectedSupplier = supplierSearchOptions.find((s) => s.id === formData.supplierId) || null;

  useEffect(() => {
    if (setNavigationBlocked && !isView) {
      setNavigationBlocked(items.length > 0);
    }
    if (isView) return;
    const handleBeforeUnload = (e) => {
      if (items.length > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.history.pushState({ isForm: true }, '', window.location.href);
    const handlePopState = () => {
      if (items.length > 0) {
        window.history.pushState({ isForm: true }, '', window.location.href);
        setCancelDialogOpen(true);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      if (setNavigationBlocked) setNavigationBlocked(false);
    };
  }, [items, isView, setNavigationBlocked]);

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleAddProductRow = (product, forceNew = false) => {
    if (!product) return;
    const behavior = company?.barcodeAfterScanBehaviorPurchase || company?.barcodeAfterScanBehavior || 'quantity';
    const dupBehavior =
      company?.duplicateItemBehaviorPurchase ||
      (behavior === 'auto_increment' ? 'auto_increment' : behavior === 'add_new' ? 'add_new' : 'dialog');

    setItems((prev) => {
      const existingIndex = prev.findIndex((it) => String(it.productId) === String(product.id));
      if (existingIndex !== -1 && !forceNew) {
        if (dupBehavior === 'auto_increment') {
          const updated = [...prev];
          const curQty = Number(updated[existingIndex].quantity || 0) || 0;
          updated[existingIndex] = {
            ...updated[existingIndex],
            quantity: String(Math.max(0.01, curQty + 1)),
          };
          setTimeout(() => {
            const el = document.getElementById('product-search-input');
            if (el) {
              el.focus();
              el.select();
            }
          }, 100);
          return updated;
        }

        if (dupBehavior !== 'add_new') {
          setDuplicateDialog({ open: true, existingIndex, product });
          if (behavior === 'search') {
            setTimeout(() => {
              const el = document.getElementById('product-search-input');
              if (el) {
                el.focus();
                el.select();
              }
            }, 120);
          } else if (behavior === 'cost' || behavior === 'price') {
            setTimeout(() => {
              const el = document.getElementById(`cost-input-${existingIndex}`);
              if (el) {
                el.focus();
                el.select();
              }
            }, 120);
          } else if (behavior === 'quantity') {
            setTimeout(() => {
              const el = document.getElementById(`qty-input-${existingIndex}`);
              if (el) {
                el.focus();
                el.select();
              }
            }, 120);
          }
          return prev;
        }
      }

      if (behavior === 'quantity') {
        setTimeout(() => {
          const el = document.getElementById('qty-input-0');
          if (el) {
            el.focus();
            el.select();
          }
        }, 120);
      } else if (behavior === 'cost' || behavior === 'price') {
        setTimeout(() => {
          const el = document.getElementById('cost-input-0');
          if (el) {
            el.focus();
            el.select();
          }
        }, 120);
      } else {
        setTimeout(() => {
          const el = document.getElementById('product-search-input');
          if (el) {
            el.focus();
            el.select();
          }
        }, 120);
      }
      return [
        {
          productId: product.id,
          productName: product.name,
          productSku: product.sku,
          productBrand: product.brand,
          requiresImei: !!product.requiresImei,
          requiresDualImei: !!product.requiresDualImei,
          imeis: [],
          quantity: 1,
          unitPrice: product.costPrice || 0,
          sellingPrice: product.sellingPrice ?? '',
          wholesalePrice: product.wholesalePrice ?? '',
          barcode: product.barcode || '',
          isReturn: false,
          isDamaged: false,
          damageReason: '',
        },
        ...prev,
      ];
    });
  };

  useEffect(() => {
    if (isView) return;
    if (focusQtyIndex == null) return;
    const el = qtyInputRefs.current[focusQtyIndex];
    if (el && typeof el.focus === 'function') el.focus();
  }, [focusQtyIndex, items.length, isView]);

  const calculateTotal = () => {
    return items.reduce((sum, item) => {
      const sign = item.isReturn ? -1 : 1;
      return sum + sign * parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0);
    }, 0);
  };

  const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const vatRate = formData.isTaxable ? toNum(company?.vatRate || 0) / 100 : 0;
  const vatIncluded = company?.purchaseVatIncluded !== false;
  const subtotal = toNum(calculateTotal());
  const isNetReturn = subtotal < 0;
  const absSub = Math.abs(subtotal);
  const discountTotal = Math.max(0, Math.min(toNum(formData.discountTotal), absSub));
  const taxableAbs = Math.max(0, absSub - discountTotal);
  const taxable = isNetReturn ? -taxableAbs : taxableAbs;

  const vatTotal =
    vatRate > 0
      ? vatIncluded
        ? taxable - taxable / (1 + vatRate)
        : taxable * vatRate
      : 0;
  const grandTotal = vatRate > 0 ? (vatIncluded ? taxable : taxable + vatTotal) : taxable;
  const roundOff = toNum(formData.roundOff);
  const totalAmount = grandTotal + roundOff;
  const isRefundOrder = totalAmount < 0;
  const absTotalAmount = Math.abs(totalAmount);

  const paidAmount = payments.reduce((sum, p) => sum + toNum(p.amount), 0);
  const balanceAmount = isRefundOrder ? Math.max(0, absTotalAmount - paidAmount) : totalAmount - paidAmount;

  // Auto-sync cash payments with total amount if single payment & autoPaymentAmount
  useEffect(() => {
    if (isView) return;
    if (!autoPaymentAmount) return;
    if (payments.length === 1 && formData.purchaseType === 'cash') {
      const target = absTotalAmount > 0 ? absTotalAmount.toFixed(dec) : '0';
      if (payments[0].amount !== target) {
        setPayments((prev) => [
          {
            ...prev[0],
            amount: target,
          },
        ]);
      }
    }
  }, [absTotalAmount, formData.purchaseType, isView, autoPaymentAmount, dec, payments.length]);

  const handleQuickMethod = (method) => {
    const target = absTotalAmount > 0 ? absTotalAmount.toFixed(dec) : '0';
    setPayments([
      {
        method,
        amount: formData.purchaseType === 'cash' ? target : (payments[0]?.amount || target),
        paymentAccountId: '',
        referenceNo: '',
      },
    ]);
    setAutoPaymentAmount(true);
  };

  const handleAddSplitPayment = () => {
    const currentPaid = payments.reduce((sum, p) => sum + toNum(p.amount), 0);
    const rem = Math.max(0, absTotalAmount - currentPaid);
    setPayments((prev) => [
      ...prev,
      {
        method: 'cash',
        paymentAccountId: '',
        referenceNo: '',
        amount: rem > 0 ? rem.toFixed(dec) : '0',
      },
    ]);
    setAutoPaymentAmount(false);
  };

  useEffect(() => {
    if (!isView && supplierIdFromUrl) {
      setFormData((prev) => ({ ...prev, supplierId: supplierIdFromUrl }));
    }
  }, [isView, supplierIdFromUrl]);

  useEffect(() => {
    if (!isView && isLpoFromUrl && !formData.lpoNumber) {
      const suggested = `LPO-${getLocalDateString().replace(/-/g, '')}-${Date.now().toString(36).toUpperCase().slice(-4)}`;
      setFormData((prev) => ({ ...prev, lpoNumber: suggested }));
    }
  }, [isView, isLpoFromUrl]);

  useEffect(() => {
    if (!isView) {
      triggerProductSearch({ limit: 50 });
    }
  }, [isView, triggerProductSearch]);

  // Keyboard shortcut (F1 / F2 / Cmd+S)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F1' || e.key === 'F2') {
        e.preventDefault();
        e.stopPropagation();
        if (productSearchInputRef.current) {
          productSearchInputRef.current.focus();
          try { productSearchInputRef.current.select(); } catch (err) {}
          return;
        }
        const el = document.getElementById('product-search-input');
        if (el) {
          el.focus();
          try { el.select(); } catch (err) {}
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);

  useEffect(() => {
    if (!scanOpen) {
      setScanError('');
      setPendingBarcode('');
    }
  }, [scanOpen]);

  useEffect(() => {
    if (!pendingBarcode) return;
    const code = String(pendingBarcode).trim();
    if (!code) return;

    const doBarcodeScan = async () => {
      let list = productSearchData?.products || [];
      let match = list.find(
        (p) =>
          String(p?.barcode || '').trim() === code ||
          String(p?.sku || '').trim() === code ||
          (Array.isArray(p?.additionalBarcodes) && p.additionalBarcodes.some((b) => String(b.barcode || '').trim() === code))
      );

      if (!match) {
        try {
          const res = await triggerProductSearch({ search: code, limit: 10 }).unwrap();
          const fetchedList = res?.products || [];
          match = fetchedList.find(
            (p) =>
              String(p?.barcode || '').trim() === code ||
              String(p?.sku || '').trim() === code ||
              (Array.isArray(p?.additionalBarcodes) && p.additionalBarcodes.some((b) => String(b.barcode || '').trim() === code))
          ) || fetchedList[0];
        } catch (e) {
          console.error('Barcode lookup error:', e);
        }
      }

      if (match) {
        handleAddProductRow(match);
        setProductSearch('');
        setPendingBarcode('');
        setScanOpen(false);
      }
    };

    doBarcodeScan();
  }, [pendingBarcode, productSearchData, handleAddProductRow, triggerProductSearch]);

  useEffect(() => {
    const val = String(productSearch).trim();
    if (!val || val.length < 2) return;
    if (searchingProducts) return;
    const list = productSearchData?.products || [];
    if (list.length === 0) return;

    const match = list.find((p) =>
      String(p?.barcode || '').trim().toLowerCase() === val.toLowerCase() ||
      String(p?.sku || '').trim().toLowerCase() === val.toLowerCase() ||
      (Array.isArray(p?.additionalBarcodes) && p.additionalBarcodes.some((b) => String(b.barcode || '').trim().toLowerCase() === val.toLowerCase()))
    );

    if (match) {
      handleAddProductRow(match);
      setProductSearch('');
    }
  }, [productSearchData, productSearch, searchingProducts, handleAddProductRow]);

  useEffect(() => {
    if (existingOrder && (isView || isEdit)) {
      setFormData((prev) => ({
        ...prev,
        invoiceNumber: existingOrder.invoiceNumber || '',
        lpoNumber: existingOrder.lpoNumber || '',
        invoiceDate: existingOrder.invoiceDate ? existingOrder.invoiceDate.substring(0, 10) : prev.invoiceDate,
        purchaseType: existingOrder.purchaseType || prev.purchaseType,
        postingType: existingOrder.postingType || prev.postingType,
        supplierId: existingOrder.supplierId || '',
        orderDate: existingOrder.orderDate ? existingOrder.orderDate.substring(0, 10) : prev.orderDate,
        notes: existingOrder.notes || '',
        discountTotal: existingOrder.discountTotal ?? 0,
        roundOff: existingOrder.roundOff ?? 0,
        paidAmount: existingOrder.paidAmount ?? 0,
        paymentMethod: existingOrder.paymentMethod || '',
        paymentReferenceNo: existingOrder.paymentReferenceNo || '',
        paymentAccountId: existingOrder.paymentAccountId || '',
        isTaxable: existingOrder.isTaxable !== false,
      }));
      setItems(
        (existingOrder.items || []).map((it) => ({
          productId: it.productId,
          quantity: it.quantity,
          unitPrice: it.unitPrice,
          sellingPrice: it.product?.sellingPrice ?? '',
          wholesalePrice: it.product?.wholesalePrice ?? '',
          productName: it.product?.name,
          productSku: it.product?.sku,
          productBrand: it.product?.brand,
          barcode: it.product?.barcode,
          requiresImei: !!it.product?.requiresImei,
          requiresDualImei: !!it.product?.requiresDualImei,
          isReturn: !!it.isReturn,
          isDamaged: !!it.isDamaged,
          damageReason: it.damageReason || '',
          imeis: (it.imeis || []).flatMap((imeiObj) => {
            if (typeof imeiObj === 'string') return [imeiObj];
            const arr = [imeiObj.imei];
            if (imeiObj.secondaryImei) arr.push(imeiObj.secondaryImei);
            return arr;
          }),
        }))
      );

      if (Array.isArray(existingOrder.payments) && existingOrder.payments.length > 0) {
        setPayments(
          existingOrder.payments.map((p) => ({
            method: p.method || 'cash',
            amount: String(p.amount ?? ''),
            paymentAccountId: p.paymentAccountId || '',
            referenceNo: p.referenceNo || '',
          }))
        );
        setAutoPaymentAmount(false);
      } else if (Number(existingOrder.paidAmount) > 0) {
        setPayments([
          {
            method: existingOrder.paymentMethod || 'cash',
            amount: String(existingOrder.paidAmount ?? ''),
            paymentAccountId: existingOrder.paymentAccountId || '',
            referenceNo: existingOrder.paymentReferenceNo || '',
          },
        ]);
        setAutoPaymentAmount(false);
      } else {
        setPayments([
          {
            method: existingOrder.paymentMethod || 'cash',
            amount: '0',
            paymentAccountId: existingOrder.paymentAccountId || '',
            referenceNo: existingOrder.paymentReferenceNo || '',
          },
        ]);
        setAutoPaymentAmount(false);
      }
    }
  }, [existingOrder, isView, isEdit]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (isView) {
      navigate('/purchases');
      return;
    }
    if (!formData.invoiceNumber || !formData.invoiceNumber.trim()) {
      alert('Invoice Number is required');
      return;
    }
    if (!formData.invoiceDate) {
      alert('Invoice Date is required');
      return;
    }
    if (!formData.supplierId) {
      alert('Supplier is required');
      return;
    }
    if (!items.length) {
      alert('Please add at least one item');
      return;
    }
    if (items.some((i) => !i.productId)) {
      alert('Please select a product for all items');
      return;
    }

    const totalPaid = payments.reduce((sum, p) => sum + toNum(p.amount), 0);

    // Validation for cash purchases vs credit purchases
    if (formData.purchaseType === 'cash' && items.length > 0) {
      if (Math.abs(totalPaid - absTotalAmount) > 0.01) {
        alert(
          `For cash purchases, total payments (${formatAmount(totalPaid)}) must match the invoice amount (${formatAmount(absTotalAmount)}).`
        );
        return;
      }
    }
    if (formData.purchaseType === 'credit' && totalPaid > absTotalAmount && absTotalAmount > 0) {
      alert(
        `Total payments (${formatAmount(totalPaid)}) cannot exceed invoice amount (${formatAmount(absTotalAmount)}).`
      );
      return;
    }

    // Validate advance payments
    const advancePaid = payments
      .filter((p) => p.method === 'advance')
      .reduce((sum, p) => sum + toNum(p.amount), 0);
    const availableAdvance = Number(selectedSupplier?.advanceBalance || 0);
    if (advancePaid > 0 && advancePaid > availableAdvance + (isEdit ? toNum(existingOrder?.paidAmount) : 0)) {
      alert(
        `Advance payment (${formatAmount(advancePaid)}) exceeds supplier's available advance balance (${formatAmount(availableAdvance)}).`
      );
      return;
    }

    const sanitizedPayments = payments
      .filter((p) => toNum(p.amount) > 0)
      .map((p) => ({
        method: p.method,
        amount: parseFloat(p.amount) || 0,
        paymentAccountId: p.paymentAccountId || null,
        referenceNo: p.referenceNo?.trim() || null,
      }));

    try {
      const payload = {
        ...formData,
        invoiceNumber: formData.invoiceNumber.trim(),
        lpoNumber: formData.lpoNumber?.trim() || undefined,
        invoiceDate: formData.invoiceDate,
        purchaseType: formData.purchaseType,
        postingType: formData.postingType,
        discountTotal: toNum(formData.discountTotal),
        roundOff: toNum(formData.roundOff),
        paidAmount: totalPaid,
        paymentMethod:
          sanitizedPayments.length > 1
            ? 'split'
            : sanitizedPayments[0]?.method || (totalPaid > 0 ? 'cash' : undefined),
        paymentAccountId: sanitizedPayments[0]?.paymentAccountId || formData.paymentAccountId || undefined,
        paymentReferenceNo: sanitizedPayments[0]?.referenceNo || formData.paymentReferenceNo || undefined,
        payments: sanitizedPayments,
        purchaseOrderNoteId: formData.purchaseOrderNoteId || undefined,
        items: items.map((item) => ({
          productId: item.productId,
          quantity: parseFloat(item.quantity),
          unitPrice: parseFloat(item.unitPrice),
          sellingPrice:
            item.sellingPrice !== '' && item.sellingPrice != null ? parseFloat(item.sellingPrice) : undefined,
          wholesalePrice:
            item.wholesalePrice !== '' && item.wholesalePrice != null ? parseFloat(item.wholesalePrice) : undefined,
          imeis: item.requiresImei ? item.imeis : undefined,
          isReturn: !!item.isReturn,
          isDamaged: !!item.isDamaged,
          damageReason: item.damageReason || undefined,
        })),
      };

      if (isEdit) {
        await updatePurchaseOrder({ id, ...payload }).unwrap();
        alert('Purchase order updated successfully!');
      } else {
        await createPurchaseOrder(payload).unwrap();
        alert('Purchase order created successfully!');
      }
      navigate('/purchases');
    } catch (error) {
      alert(error?.data?.message || (isEdit ? 'Error updating purchase order' : 'Error creating purchase order'));
    }
  };

  const totalItemCount = items.length;
  const returnItemCount = items.filter((i) => i.isReturn).length;
  const purchaseItemCount = totalItemCount - returnItemCount;
  const totalQuantitySum = items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);

  return (
    <Box sx={{ pb: 14, maxWidth: 1440, mx: 'auto' }}>
      {/* Top Header & Breadcrumb Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton
            onClick={() => navigate('/purchases')}
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              '&:hover': { bgcolor: 'grey.100' },
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.02em' }}>
                {isView ? 'Purchase Order Details' : isEdit ? `Edit Purchase #${existingOrder?.orderNumber || ''}` : poNoteData ? `Purchase from PO: ${poNoteData.poNumber}` : 'New Purchase Invoice'}
              </Typography>
              <Chip
                size="small"
                label={isView ? (existingOrder?.status || 'View Mode').toUpperCase() : isEdit ? 'EDITING' : formData.purchaseType === 'cash' ? 'Cash Purchase' : 'Credit Purchase'}
                color={isView ? 'default' : isEdit ? 'warning' : formData.purchaseType === 'cash' ? 'success' : 'primary'}
                sx={{ fontWeight: 700, fontSize: '0.72rem', height: 22 }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {isView
                ? `Order #${existingOrder?.orderNumber || ''} • Supplier: ${existingOrder?.supplier?.name || '–'}`
                : isEdit
                ? `Editing order #${existingOrder?.orderNumber || ''} • Correcting stock and accounts`
                : 'Enter supplier invoice details, scan/search items, and record purchase stock & payment'}
            </Typography>
          </Box>
        </Box>

        {/* View Mode Status & Actions */}
        {isView && existingOrder && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField
              select
              size="small"
              label="Status"
              value={existingOrder.status ?? ''}
              onChange={(e) => {
                const newStatus = e.target.value;
                if (newStatus && id) updatePurchaseOrderStatus({ id, status: newStatus });
              }}
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="received">Received</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </TextField>
            <Button
              variant="contained"
              color="warning"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/purchases/orders/${id}/edit`)}
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
            >
              Edit Purchase
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              onClick={() => {
                setBarcodeSelection(items.filter((i) => !i.isReturn).map((i) => ({ ...i, printQty: i.quantity, selected: true })));
                setBarcodeDialogOpen(true);
              }}
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
            >
              Print Barcodes
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
            >
              Delete
            </Button>
          </Box>
        )}
      </Box>

      {/* Conversion Banner from PO */}
      {poNoteData && !isView && (
        <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2, border: '1px solid #bbf7d0', bgcolor: '#f0fdf4' }}>
          <strong>Converting from Purchase Order {poNoteData.poNumber}:</strong> Supplier and {poNoteData.items?.length || 0} items have been pre-filled. Enter the supplier invoice number and payment details to record this purchase.
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        {/* Section 1: Supplier & Invoice Information Card */}
        <Card
          elevation={0}
          sx={{
            mb: 2.5,
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            bgcolor: 'background.paper',
          }}
        >
          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <BusinessIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                1. Supplier & Invoice Details
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {/* Row 1 - Column 1: Supplier Selection */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={supplierSearchOptions}
                    loading={searchingSuppliers}
                    getOptionLabel={(opt) => opt?.name || ''}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    value={selectedSupplier}
                    onChange={(_, newValue) => {
                      setFormData({ ...formData, supplierId: newValue?.id || '' });
                    }}
                    onInputChange={(_, newInputValue) => {
                      if (newInputValue && newInputValue.length >= 2) {
                        triggerSupplierSearch({ search: newInputValue, limit: 20 });
                      } else if (!newInputValue) {
                        triggerSupplierSearch({ limit: 20 });
                      }
                    }}
                    onOpen={() => {
                      if (supplierSearchOptions.length === 0) triggerSupplierSearch({ limit: 20 });
                    }}
                    disabled={isView}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Supplier *"
                        placeholder="Search supplier..."
                        required={!formData.supplierId}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <BusinessIcon fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                  {!isView && (
                    <Tooltip title="Add New Supplier" arrow>
                      <IconButton
                        color="primary"
                        onClick={() => setSupplierDialogOpen(true)}
                        sx={{
                          height: 40,
                          width: 40,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          flexShrink: 0,
                          bgcolor: 'grey.50',
                          '&:hover': { bgcolor: 'primary.50', borderColor: 'primary.main' },
                        }}
                      >
                        <PersonAddAltIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                {/* Advance Balance Badge */}
                {selectedSupplier && Number(selectedSupplier.advanceBalance || 0) > 0 && (
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      size="small"
                      color="info"
                      variant="outlined"
                      icon={<InfoOutlinedIcon />}
                      label={`Advance: ${formatAmount(selectedSupplier.advanceBalance)}`}
                      sx={{ fontWeight: 600, fontSize: '0.72rem', height: 20 }}
                    />
                  </Box>
                )}
              </Grid>

              {/* Row 1 - Column 2: Invoice Number */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Supplier Invoice No. *"
                  name="invoiceNumber"
                  placeholder="e.g. INV-2024-8849"
                  value={formData.invoiceNumber}
                  onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                  disabled={isView}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ReceiptIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Row 1 - Column 3: Invoice Date */}
              <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Invoice Date *"
                  name="invoiceDate"
                  value={formData.invoiceDate}
                  onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  disabled={isView}
                  required
                />
              </Grid>

              {/* Row 1 - Column 4: Payment Due Date */}
              <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Due Date"
                  name="dueDate"
                  value={formData.dueDate || formData.invoiceDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  disabled={formData.purchaseType === 'cash' || isView}
                />
              </Grid>

              {/* Row 2 - Column 1: Purchase Type Toggle (Cash vs Credit) */}
              <Grid size={{ xs: 12, md: 4 }}>
                <ToggleButtonGroup
                  fullWidth
                  size="small"
                  value={formData.purchaseType}
                  exclusive
                  onChange={(_, next) => {
                    if (!next) return;
                    setFormData((prev) => ({
                      ...prev,
                      purchaseType: next,
                    }));
                    if (next === 'cash') {
                      const target = absTotalAmount > 0 ? absTotalAmount.toFixed(dec) : '0';
                      setPayments((prev) => [
                        {
                          method: prev[0]?.method || 'cash',
                          amount: target,
                          paymentAccountId: prev[0]?.paymentAccountId || '',
                          referenceNo: prev[0]?.referenceNo || '',
                        },
                      ]);
                      setAutoPaymentAmount(true);
                    }
                  }}
                  disabled={isView}
                  sx={{
                    height: 40,
                    '& .MuiToggleButton-root': {
                      height: 40,
                      py: 0,
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      textTransform: 'none',
                      border: '1px solid',
                      borderColor: 'divider',
                    },
                  }}
                >
                  <ToggleButton
                    value="cash"
                    sx={{
                      '&.Mui-selected': {
                        bgcolor: '#f0fdf4 !important',
                        color: '#166534 !important',
                        borderColor: '#86efac !important',
                      },
                    }}
                  >
                    <AttachMoneyIcon fontSize="small" sx={{ mr: 0.5 }} /> Cash Purchase
                  </ToggleButton>
                  <ToggleButton
                    value="credit"
                    sx={{
                      '&.Mui-selected': {
                        bgcolor: '#eff6ff !important',
                        color: '#1e40af !important',
                        borderColor: '#93c5fd !important',
                      },
                    }}
                  >
                    <CreditCardIcon fontSize="small" sx={{ mr: 0.5 }} /> Credit Purchase
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>

              {/* Row 2 - Column 2: Posting Type */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="Posting Type"
                  name="postingType"
                  value={formData.postingType}
                  onChange={(e) => setFormData({ ...formData, postingType: e.target.value })}
                  disabled={isView}
                >
                  <MenuItem value="inventory">📦 Inventory (Stock In)</MenuItem>
                  <MenuItem value="expense">💼 Expense (Write-off)</MenuItem>
                </TextField>
              </Grid>

              {/* Row 2 - Column 3: LPO Reference */}
              <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="LPO Reference No."
                  name="lpoNumber"
                  value={formData.lpoNumber}
                  onChange={(e) => setFormData({ ...formData, lpoNumber: e.target.value })}
                  placeholder="e.g. LPO-001"
                  disabled={isView}
                />
              </Grid>

              {/* Row 2 - Column 4: Taxable Toggle */}
              <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                <Box
                  sx={{
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.75,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s',
                    '&:hover': {
                      borderColor: 'text.secondary',
                    },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem', color: 'text.secondary' }}>
                    Taxable (VAT)
                  </Typography>
                  <Switch
                    size="small"
                    checked={formData.isTaxable}
                    onChange={(e) => setFormData({ ...formData, isTaxable: e.target.checked })}
                    disabled={isView}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Section 2: Items Search & Scanning Card */}
        <Card
          elevation={0}
          sx={{
            mb: 2.5,
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          {/* Item Search Bar Header */}
          <Box
            sx={{
              p: 2,
              bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : '#f8fafc'),
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShoppingBagIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  2. Purchase Order Items ({totalItemCount} {totalItemCount === 1 ? 'Product' : 'Products'})
                </Typography>
              </Box>
              <Chip
                size="small"
                variant="outlined"
                label="Hotkey: Press [F1] to Scan/Search"
                sx={{ fontSize: '0.72rem', fontWeight: 600, bgcolor: 'background.paper' }}
              />
            </Box>

            {!isView && (
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                <Autocomplete
                  fullWidth
                  size="small"
                  autoHighlight
                  freeSolo
                  options={searchingProducts ? [] : productSearchData?.products || []}
                  loading={searchingProducts}
                  filterOptions={(x) => x}
                  value={null}
                  inputValue={productSearch}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    if (typeof option === 'string') return option;
                    return formatItemDisplayName(
                      { name: option.name, sku: option.sku || option.barcode, brand: option.brand },
                      itemNameFormat === 'name' ? 'name_sku' : itemNameFormat
                    );
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', py: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {option.name}
                          </Typography>
                          {option.costPrice != null && (
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', bgcolor: '#eff6ff', px: 1, py: 0.2, borderRadius: 1 }}>
                              Cost: {formatAmount(option.costPrice)}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {option.barcode ? `Barcode: ${option.barcode}` : ''}
                          {option.sku ? ` • SKU: ${option.sku}` : ''}
                          {option.brand ? ` • Brand: ${option.brand}` : ''}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  onInputChange={(_, value) => {
                    setProductSearch(value);
                    if (value && value.trim().length >= 1) {
                      triggerProductSearch({ search: value.trim(), limit: 50 });
                    } else if (!value) {
                      triggerProductSearch({ limit: 50 });
                    }
                  }}
                  onChange={(_, value) => {
                    if (value && typeof value === 'object') {
                      handleAddProductRow(value);
                      setProductSearch('');
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputRef={productSearchInputRef}
                      inputProps={{
                        ...params.inputProps,
                        id: 'product-search-input',
                      }}
                      id="product-search-input"
                      autoFocus
                      placeholder="Scan barcode or search by item name, SKU, brand [F1]..."
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const typed = String(productSearch).trim();
                          if (!typed) return;

                          let list = productSearchData?.products || [];
                          if (list.length === 0 || searchingProducts) {
                            try {
                              const res = await triggerProductSearch({ search: typed, limit: 20 }).unwrap();
                              list = res?.products || [];
                            } catch (err) {
                              console.error(err);
                            }
                          }

                          if (list.length > 0) {
                            const match =
                              list.find((p) => {
                                const bMatch = String(p?.barcode || '').trim().toLowerCase() === typed.toLowerCase();
                                const skuMatch = String(p?.sku || '').trim().toLowerCase() === typed.toLowerCase();
                                const nameMatch = p?.name && p.name.toLowerCase() === typed.toLowerCase();
                                const addlMatch =
                                  Array.isArray(p?.additionalBarcodes) &&
                                  p.additionalBarcodes.some(
                                    (b) => String(b.barcode || '').trim().toLowerCase() === typed.toLowerCase()
                                  );
                                return bMatch || skuMatch || nameMatch || addlMatch;
                              }) ||
                              list.find((p) => {
                                const nameIncludes = p?.name && p.name.toLowerCase().includes(typed.toLowerCase());
                                const brandIncludes = p?.brand && p.brand.toLowerCase().includes(typed.toLowerCase());
                                return nameIncludes || brandIncludes;
                              }) ||
                              list[0];

                            if (match) {
                              handleAddProductRow(match);
                              setProductSearch('');
                            }
                          }
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'background.paper',
                          borderRadius: 2,
                        },
                      }}
                    />
                  )}
                />

                <Button
                  variant="outlined"
                  startIcon={<QrCodeScannerIcon />}
                  onClick={() => setScanOpen(true)}
                  sx={{ whiteSpace: 'nowrap', borderRadius: 2, height: 40, px: 2, fontWeight: 600, textTransform: 'none' }}
                >
                  Scan
                </Button>

                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setProductDialog({ open: true, productId: null })}
                  sx={{ whiteSpace: 'nowrap', borderRadius: 2, height: 40, px: 2, fontWeight: 700, textTransform: 'none' }}
                >
                  New Product
                </Button>
              </Box>
            )}
          </Box>

          {/* Table Container */}
          <TableContainer>
            <Table size="small" sx={{ minWidth: 780 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.800' : '#f1f5f9') }}>
                  <TableCell sx={{ fontWeight: 700, width: 44, textAlign: 'center', color: 'text.secondary' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Product Description</TableCell>
                  <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 140, color: 'text.secondary' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 130, color: 'text.secondary' }}>Quantity</TableCell>
                  <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 120, color: 'text.secondary' }}>{`Unit Cost (${currency})`}</TableCell>
                  <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 120, color: 'text.secondary' }}>{`Selling (${currency})`}</TableCell>
                  {showWholesalePrice && (
                    <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 120, color: 'text.secondary' }}>{`Wholesale (${currency})`}</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 700, textAlign: 'right', width: 120, color: 'text.secondary' }}>{`Total (${currency})`}</TableCell>
                  <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 120, color: 'text.secondary' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={showWholesalePrice ? 9 : 8} sx={{ py: 6, textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <ShoppingBagIcon sx={{ fontSize: 44, color: 'text.disabled' }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          No items in this purchase order
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          Scan a barcode or use the search bar above to add products to the invoice.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, index) => {
                    const lineTotal = parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0);
                    const reqQty = (parseFloat(item.quantity) || 0) * (item.requiresDualImei ? 2 : 1);
                    const imeiComplete = (item.imeis?.length || 0) === reqQty;

                    return (
                      <TableRow
                        key={index}
                        hover
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          bgcolor: item.isReturn ? 'rgba(239, 68, 68, 0.04)' : 'inherit',
                          transition: 'background-color 0.2s',
                        }}
                      >
                        {/* Row Index */}
                        <TableCell sx={{ fontWeight: 700, textAlign: 'center', color: 'text.secondary' }}>
                          {index + 1}
                        </TableCell>

                        {/* Product Name & SKU */}
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: item.isReturn ? 'error.dark' : 'text.primary' }}>
                            {item.productName || 'Product'}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mt: 0.25, flexWrap: 'wrap' }}>
                            {item.productSku && (
                              <Chip
                                size="small"
                                label={`SKU: ${item.productSku}`}
                                sx={{ height: 18, fontSize: '0.68rem', fontWeight: 600, bgcolor: 'grey.100' }}
                              />
                            )}
                            {item.barcode && (
                              <Chip
                                size="small"
                                label={`Barcode: ${item.barcode}`}
                                sx={{ height: 18, fontSize: '0.68rem', fontWeight: 500, bgcolor: 'grey.100' }}
                              />
                            )}
                            {item.productBrand && (
                              <Chip
                                size="small"
                                label={item.productBrand}
                                sx={{ height: 18, fontSize: '0.68rem', fontWeight: 600, color: 'primary.main', bgcolor: '#eff6ff' }}
                              />
                            )}
                            {item.isReturn && (
                              <Chip
                                size="small"
                                label="RETURN TO SUPPLIER"
                                color="error"
                                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 800 }}
                              />
                            )}
                          </Box>
                        </TableCell>

                        {/* Type / Return Column */}
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                              size="small"
                              label={item.isReturn ? '↩ Return' : '📥 Purchase'}
                              color={item.isReturn ? 'error' : 'success'}
                              variant={item.isReturn ? 'filled' : 'outlined'}
                              onClick={
                                !isView
                                  ? () => {
                                      const nextReturn = !item.isReturn;
                                      handleItemChange(index, 'isReturn', nextReturn);
                                      if (!nextReturn) {
                                        handleItemChange(index, 'isDamaged', false);
                                        handleItemChange(index, 'damageReason', '');
                                      }
                                    }
                                  : undefined
                              }
                              clickable={!isView}
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.72rem',
                                height: 24,
                                cursor: isView ? 'default' : 'pointer',
                              }}
                            />
                            {item.isReturn && (
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      size="small"
                                      color="error"
                                      checked={!!item.isDamaged}
                                      onChange={(e) => handleItemChange(index, 'isDamaged', e.target.checked)}
                                      disabled={isView}
                                      sx={{ p: 0.25 }}
                                    />
                                  }
                                  label={
                                    <Typography variant="caption" color="error" sx={{ fontWeight: 700, fontSize: '0.68rem' }}>
                                      Damaged
                                    </Typography>
                                  }
                                  sx={{ m: 0 }}
                                />
                                {item.isDamaged && (
                                  <TextField
                                    placeholder="Reason..."
                                    size="small"
                                    value={item.damageReason || ''}
                                    onChange={(e) => handleItemChange(index, 'damageReason', e.target.value)}
                                    disabled={isView}
                                    sx={{
                                      width: 110,
                                      '& .MuiInputBase-input': { fontSize: '0.68rem', p: '2px 6px' },
                                    }}
                                  />
                                )}
                              </Box>
                            )}
                          </Box>
                        </TableCell>

                        {/* Quantity with Stepper */}
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const q = parseFloat(item.quantity) || 0;
                                handleItemChange(index, 'quantity', String(Math.max(0.01, q - 1)));
                              }}
                              disabled={isView}
                              sx={{ border: '1px solid', borderColor: 'divider', p: 0.5, borderRadius: 1 }}
                            >
                              <RemoveIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                            <TextField
                              id={`qty-input-${index}`}
                              type="number"
                              size="small"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                              disabled={isView}
                              sx={{
                                width: 70,
                                '& input': { textAlign: 'center', fontWeight: 700, py: 0.5, px: 0.5, fontSize: '0.88rem' },
                              }}
                              inputProps={{ min: 0.01, step: 0.01 }}
                              inputRef={(el) => {
                                qtyInputRefs.current[index] = el;
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => {
                                const q = parseFloat(item.quantity) || 0;
                                handleItemChange(index, 'quantity', String(q + 1));
                              }}
                              disabled={isView}
                              sx={{ border: '1px solid', borderColor: 'divider', p: 0.5, borderRadius: 1 }}
                            >
                              <AddIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        </TableCell>

                        {/* Unit Cost */}
                        <TableCell sx={{ textAlign: 'center' }}>
                          <TextField
                            id={`cost-input-${index}`}
                            type="number"
                            size="small"
                            value={item.unitPrice}
                            onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                            disabled={isView}
                            sx={{ width: 100, '& input': { textAlign: 'right', fontWeight: 600, py: 0.5 } }}
                            inputProps={{ min: 0, step: 0.01 }}
                            placeholder="0.00"
                          />
                        </TableCell>

                        {/* Selling Price */}
                        <TableCell sx={{ textAlign: 'center' }}>
                          <TextField
                            type="number"
                            size="small"
                            value={item.sellingPrice ?? ''}
                            onChange={(e) => handleItemChange(index, 'sellingPrice', e.target.value)}
                            disabled={item.isReturn || isView}
                            sx={{ width: 100, '& input': { textAlign: 'right', fontWeight: 600, py: 0.5 } }}
                            inputProps={{ min: 0, step: 0.01 }}
                            placeholder={item.isReturn ? 'N/A' : '0.00'}
                          />
                        </TableCell>

                        {/* Wholesale Price */}
                        {showWholesalePrice && (
                          <TableCell sx={{ textAlign: 'center' }}>
                            <TextField
                              type="number"
                              size="small"
                              value={item.wholesalePrice ?? ''}
                              onChange={(e) => handleItemChange(index, 'wholesalePrice', e.target.value)}
                              disabled={item.isReturn || isView}
                              sx={{ width: 100, '& input': { textAlign: 'right', fontWeight: 600, py: 0.5 } }}
                              inputProps={{ min: 0, step: 0.01 }}
                              placeholder={item.isReturn ? 'N/A' : '0.00'}
                            />
                          </TableCell>
                        )}

                        {/* Line Total */}
                        <TableCell
                          sx={{
                            fontWeight: 800,
                            textAlign: 'right',
                            fontSize: '0.9rem',
                            color: item.isReturn ? 'error.main' : 'primary.dark',
                          }}
                        >
                          {item.isReturn ? `-${formatAmount(lineTotal)}` : formatAmount(lineTotal)}
                        </TableCell>

                        {/* Actions & IMEI */}
                        <TableCell sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                          {item.requiresImei && (
                            <Tooltip title={imeiComplete ? 'All required IMEIs provided' : 'IMEIs missing / incomplete'} arrow>
                              <Button
                                size="small"
                                variant={imeiComplete ? 'contained' : 'outlined'}
                                color={imeiComplete ? 'success' : 'warning'}
                                onClick={() =>
                                  setImeiModal({
                                    open: true,
                                    itemIndex: index,
                                    imeis: item.imeis || [],
                                    text: (item.imeis || []).join('\n'),
                                  })
                                }
                                sx={{
                                  mr: 0.75,
                                  height: 28,
                                  fontSize: '0.72rem',
                                  fontWeight: 700,
                                  textTransform: 'none',
                                  borderRadius: 1,
                                }}
                              >
                                {item.imeis?.length || 0}/{reqQty} IMEI
                              </Button>
                            </Tooltip>
                          )}
                          <Tooltip title="Edit Product Details" arrow>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => setProductDialog({ open: true, productId: item.productId })}
                              sx={{ mr: 0.5, p: 0.5 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!isView && (
                            <Tooltip title="Remove Item" arrow>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveItem(index)}
                                sx={{ p: 0.5 }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Section 3: Bottom Two-Column Split (Payment & Financial Summary) */}
        <Grid container spacing={2.5}>
          {/* Left Column: Payment & Notes */}
          <Grid size={{ xs: 12, md: 7 }}>
            {/* Payment Card */}
            <Card
              elevation={0}
              sx={{
                mb: 2.5,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                bgcolor: 'background.paper',
              }}
            >
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountBalanceIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      3. Payment & Settlement
                    </Typography>
                  </Box>
                  {payments.length > 1 && (
                    <Chip
                      size="small"
                      label={`Split Payment (${payments.length} methods)`}
                      color="secondary"
                      sx={{ fontWeight: 700, fontSize: '0.72rem' }}
                    />
                  )}
                </Box>

                {Number(selectedSupplier?.advanceBalance || 0) > 0 && (
                  <Alert
                    severity="info"
                    sx={{
                      mb: 2,
                      borderRadius: 1.5,
                      border: '1px solid #bfdbfe',
                      bgcolor: '#eff6ff',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    Supplier has an advance balance of <strong>{formatAmount(selectedSupplier.advanceBalance)}</strong>. You can choose <strong>Advance</strong> payment method.
                  </Alert>
                )}

                {/* Quick Method Buttons */}
                {!isView && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.75, display: 'block' }}>
                      Quick Payment Method
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(5, 1fr)' }, gap: 1 }}>
                      <Button
                        variant={payments.length === 1 && payments[0].method === 'cash' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => handleQuickMethod('cash')}
                        startIcon={<AttachMoneyIcon sx={{ fontSize: 15 }} />}
                        sx={{ textTransform: 'none', fontWeight: 700, py: 0.6, fontSize: '0.78rem', borderRadius: 1.5 }}
                      >
                        Cash
                      </Button>
                      <Button
                        variant={payments.length === 1 && payments[0].method === 'bank' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => handleQuickMethod('bank')}
                        startIcon={<AccountBalanceIcon sx={{ fontSize: 15 }} />}
                        sx={{ textTransform: 'none', fontWeight: 700, py: 0.6, fontSize: '0.78rem', borderRadius: 1.5 }}
                      >
                        Bank
                      </Button>
                      <Button
                        variant={payments.length === 1 && payments[0].method === 'card' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => handleQuickMethod('card')}
                        startIcon={<CreditCardIcon sx={{ fontSize: 15 }} />}
                        sx={{ textTransform: 'none', fontWeight: 700, py: 0.6, fontSize: '0.78rem', borderRadius: 1.5 }}
                      >
                        Card
                      </Button>
                      <Button
                        variant={payments.length === 1 && payments[0].method === 'cheque' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => handleQuickMethod('cheque')}
                        startIcon={<ReceiptIcon sx={{ fontSize: 15 }} />}
                        sx={{ textTransform: 'none', fontWeight: 700, py: 0.6, fontSize: '0.78rem', borderRadius: 1.5 }}
                      >
                        Cheque
                      </Button>
                      <Button
                        variant={payments.length === 1 && payments[0].method === 'advance' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => handleQuickMethod('advance')}
                        startIcon={<AccountBalanceWalletIcon sx={{ fontSize: 15 }} />}
                        disabled={!selectedSupplier || Number(selectedSupplier.advanceBalance || 0) <= 0}
                        sx={{ textTransform: 'none', fontWeight: 700, py: 0.6, fontSize: '0.78rem', borderRadius: 1.5 }}
                      >
                        Advance
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Payments List */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {payments.map((p, index) => {
                    const isAdvance = p.method === 'advance';
                    const advBalance = Number(selectedSupplier?.advanceBalance || 0);
                    const advExceeded = isAdvance && toNum(p.amount) > advBalance;

                    return (
                      <Box
                        key={index}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.800' : '#f8fafc'),
                          border: '1px solid',
                          borderColor: advExceeded ? 'error.light' : 'divider',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.25,
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
                          <TextField
                            select
                            size="small"
                            label="Method *"
                            value={p.method}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].method = e.target.value;
                              setPayments(next);
                              setAutoPaymentAmount(false);
                            }}
                            sx={{ flex: 1.2, minWidth: 120 }}
                            disabled={isView}
                          >
                            <MenuItem value="cash">💵 Cash</MenuItem>
                            <MenuItem value="bank">🏦 Bank Transfer</MenuItem>
                            <MenuItem value="card">💳 Card / POS</MenuItem>
                            <MenuItem value="cheque">📑 Cheque</MenuItem>
                            <MenuItem value="advance">⭐ Supplier Advance</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </TextField>

                          <TextField
                            type="number"
                            size="small"
                            label={`Amount (${currency}) *`}
                            value={p.amount}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].amount = e.target.value;
                              setPayments(next);
                              setAutoPaymentAmount(false);
                            }}
                            error={advExceeded}
                            helperText={advExceeded ? `Exceeds advance (${formatAmount(advBalance)})` : ''}
                            sx={{ flex: 1, minWidth: 110, '& input': { fontWeight: 700 } }}
                            inputProps={{ step: 0.01, min: 0 }}
                            disabled={isView}
                          />

                          {!isView && payments.length > 1 && (
                            <Tooltip title="Remove Payment" arrow>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setPayments(payments.filter((_, i) => i !== index));
                                  setAutoPaymentAmount(false);
                                }}
                                sx={{ p: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1.25 }}>
                          <TextField
                            select
                            size="small"
                            label="Payment Ledger Account"
                            value={p.paymentAccountId || ''}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].paymentAccountId = e.target.value;
                              setPayments(next);
                              setAutoPaymentAmount(false);
                            }}
                            sx={{ flex: 1.2 }}
                            disabled={isView}
                          >
                            <MenuItem value="">Auto Account (Selected by method)</MenuItem>
                            {paymentAccounts?.map((acc) => (
                              <MenuItem key={acc.id} value={acc.id}>
                                {acc.code} - {acc.name}
                              </MenuItem>
                            ))}
                          </TextField>

                          <TextField
                            size="small"
                            label="Reference No."
                            placeholder="Cheque No / Transfer Ref"
                            value={p.referenceNo || ''}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].referenceNo = e.target.value;
                              setPayments(next);
                              setAutoPaymentAmount(false);
                            }}
                            sx={{ flex: 1 }}
                            disabled={isView}
                          />
                        </Box>

                        {isAdvance && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography variant="caption" sx={{ color: advExceeded ? 'error.main' : 'success.main', fontWeight: 600 }}>
                              Available Advance Credit: {formatAmount(advBalance)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  })}

                  {/* Add Split Payment Button */}
                  {!isView && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 0.5 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddSplitPayment}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          borderRadius: 1.5,
                          borderStyle: 'dashed',
                        }}
                      >
                        + Split Payment
                      </Button>

                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Total Paid: <strong>{formatAmount(paidAmount)}</strong> / {formatAmount(absTotalAmount)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Notes Card */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                bgcolor: 'background.paper',
              }}
            >
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Additional Notes / Delivery Remarks
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  name="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Enter any supplier terms, delivery details, or reference remarks..."
                  multiline
                  rows={2}
                  disabled={isView}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column: Financial Summary Card */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: '1.5px solid',
                borderColor: isRefundOrder ? 'error.light' : 'primary.light',
                boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                bgcolor: 'background.paper',
                position: 'sticky',
                top: 24,
              }}
            >
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalculateIcon color={isRefundOrder ? 'error' : 'primary'} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      Financial Summary
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {returnItemCount > 0 && (
                      <Chip
                        size="small"
                        label={`${returnItemCount} Return${returnItemCount > 1 ? 's' : ''}`}
                        color="error"
                        sx={{ fontWeight: 700, height: 22, fontSize: '0.68rem' }}
                      />
                    )}
                    <Chip
                      size="small"
                      label={`${totalItemCount} Items • ${totalQuantitySum} Qty`}
                      sx={{ fontWeight: 700, bgcolor: 'grey.100', height: 22, fontSize: '0.68rem' }}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {/* Subtotal */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Subtotal {returnItemCount > 0 ? '(Net)' : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: subtotal < 0 ? 'error.main' : 'inherit' }}>
                      {formatAmount(subtotal)}
                    </Typography>
                  </Box>

                  {/* Discount Input */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Bill Discount
                    </Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={formData.discountTotal}
                      onChange={(e) => setFormData({ ...formData, discountTotal: e.target.value })}
                      disabled={isView}
                      sx={{ width: 110, '& input': { textAlign: 'right', py: 0.5, fontWeight: 600 } }}
                      inputProps={{ min: 0, step: 0.01 }}
                      placeholder="0.00"
                    />
                  </Box>

                  {/* VAT Total */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="body2" color="text.secondary">
                        VAT Total
                      </Typography>
                      {formData.isTaxable && (
                        <Chip
                          size="small"
                          label={`${company?.vatRate || 5}% ${vatIncluded ? 'Inc' : 'Ex'}`}
                          sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: vatTotal < 0 ? 'error.main' : 'inherit' }}>
                      {formatAmount(vatTotal)}
                    </Typography>
                  </Box>

                  {/* Round Off */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Round Off
                    </Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={formData.roundOff}
                      onChange={(e) => setFormData({ ...formData, roundOff: e.target.value })}
                      disabled={isView}
                      sx={{ width: 110, '& input': { textAlign: 'right', py: 0.5, fontWeight: 600 } }}
                      inputProps={{ step: 0.01 }}
                      placeholder="0.00"
                    />
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  {/* Grand Total Highlight */}
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? isRefundOrder
                            ? 'rgba(239, 68, 68, 0.15)'
                            : 'rgba(25, 118, 210, 0.15)'
                          : isRefundOrder
                          ? '#fef2f2'
                          : '#eef7fe',
                      border: '1.5px solid',
                      borderColor: isRefundOrder ? 'error.light' : 'primary.light',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color={isRefundOrder ? 'error.main' : 'text.secondary'}
                        sx={{ fontWeight: 700, textTransform: 'uppercase' }}
                      >
                        {isRefundOrder ? 'Net Supplier Refund (Credit Note)' : 'Net Grand Total'}
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 900, color: isRefundOrder ? 'error.main' : 'primary.main', lineHeight: 1.1 }}
                      >
                        {formatAmount(absTotalAmount)}
                      </Typography>
                    </Box>
                    <Chip
                      label={isRefundOrder ? 'REFUND' : currency}
                      color={isRefundOrder ? 'error' : 'primary'}
                      sx={{ fontWeight: 800, fontSize: '0.78rem' }}
                    />
                  </Box>

                  {/* Paid & Balance Breakdown */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {isRefundOrder ? 'Refund Received' : 'Paid Amount'}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#166534' }}>
                      {formatAmount(paidAmount)}
                    </Typography>
                  </Box>

                  {payments.length > 1 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
                      {payments
                        .filter((p) => toNum(p.amount) > 0)
                        .map((p, i) => (
                          <Chip
                            key={i}
                            size="small"
                            label={`${p.method.toUpperCase()}: ${formatAmount(p.amount)}`}
                            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                          />
                        ))}
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {isRefundOrder ? 'Refund Due' : 'Balance Due'}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 800,
                        color: balanceAmount > 0 ? (isRefundOrder ? 'info.main' : 'error.main') : 'success.main',
                      }}
                    >
                      {formatAmount(balanceAmount)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Sticky Floating Bottom Action Bar */}
        <Paper
          elevation={4}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            py: 1.5,
            px: 3,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            zIndex: 1100,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="caption" color="text.secondary">
                Order Summary
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {totalItemCount} Items ({totalQuantitySum} Qty)
                {returnItemCount > 0 ? ` • ${returnItemCount} Return` : ''} • Total: {formatAmount(absTotalAmount)}
                {isRefundOrder ? ' (Refund)' : ''}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            {isView && (
              <Button
                variant="contained"
                color="warning"
                onClick={() => navigate(`/purchases/orders/${id}/edit`)}
                startIcon={<EditIcon />}
                sx={{ borderRadius: 1.5, fontWeight: 700, px: 2.5 }}
              >
                Edit Purchase
              </Button>
            )}
            {!isView && (
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => setCancelDialogOpen(true)}
                sx={{ borderRadius: 1.5, fontWeight: 600, px: 2.5 }}
              >
                Cancel
              </Button>
            )}
            <Button
              type={isView ? 'button' : 'submit'}
              variant="contained"
              color="primary"
              disabled={(!isView && items.length === 0) || isSubmitting}
              onClick={isView ? () => navigate('/purchases') : undefined}
              startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              sx={{
                borderRadius: 1.5,
                fontWeight: 800,
                px: 3.5,
                py: 1,
                boxShadow: '0 4px 14px rgba(25, 118, 210, 0.3)',
              }}
            >
              {isSubmitting ? 'Saving...' : isView ? 'Back to Purchases' : isEdit ? 'Update Purchase Invoice' : 'Create Purchase Invoice'}
            </Button>
          </Box>
        </Paper>
      </form>

      {/* Barcode Camera Scanner Modal */}
      <Dialog open={scanOpen} onClose={() => setScanOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Scan Item Barcode</DialogTitle>
        <DialogContent>
          {scanError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {scanError}
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Point the camera at the barcode. The item will be added to the order when matched.
            </Alert>
          )}
          {scanOpen && (
            <BarcodeScannerComponent
              width="100%"
              height={360}
              delay={200}
              constraints={{ facingMode: 'environment' }}
              onUpdate={(err, result) => {
                if (err) {
                  if (err?.name === 'NotAllowedError') {
                    setScanError('Camera permission denied. Please allow camera access and try again.');
                  } else if (err?.name !== 'NotFoundException') {
                    setScanError('Unable to read barcode. Try better lighting or move closer.');
                  }
                  return;
                }
                const text = result?.text ? String(result.text).trim() : '';
                if (!text) return;
                setProductSearch(text);
                setPendingBarcode(text);
                triggerProductSearch({ search: text, limit: 20 });
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setScanOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Supplier Modal */}
      <Dialog
        open={supplierDialogOpen}
        onClose={() => setSupplierDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 12px 40px rgba(0,0,0,0.15)' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add Supplier</DialogTitle>
        <DialogContent
          sx={{
            pt: 2,
            overflow: 'visible',
            '& .MuiInputLabel-root': { overflow: 'visible' },
          }}
        >
          <SupplierFormContent
            compact
            onCancel={() => setSupplierDialogOpen(false)}
            onSuccess={(created) => {
              setSupplierDialogOpen(false);
              if (created?.id) {
                setFormData((prev) => ({ ...prev, supplierId: created.id }));
              }
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Duplicate Item Dialog */}
      <Dialog
        open={duplicateDialog.open}
        onClose={() => setDuplicateDialog({ open: false, existingIndex: -1, product: null })}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Item already in list</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            {duplicateDialog.product
              ? `“${duplicateDialog.product.name}${duplicateDialog.product.sku ? ` (${duplicateDialog.product.sku})` : ''}” is already in the list.`
              : 'This item is already in the list.'}
          </Typography>
          <Typography color="text.secondary">
            Do you want to increase the quantity by 1, or add it as a new line?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setDuplicateDialog({ open: false, existingIndex: -1, product: null })}
          >
            No
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              const product = duplicateDialog.product;
              setDuplicateDialog({ open: false, existingIndex: -1, product: null });
              setProductSearch('');
              setPendingBarcode('');
              setScanOpen(false);
              if (product) {
                handleAddProductRow(product, true);
              }
            }}
          >
            Add as new
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const idx = duplicateDialog.existingIndex;
              setItems((prev) => {
                if (idx < 0 || idx >= prev.length) return prev;
                const next = [...prev];
                const currentQty = Number(next[idx]?.quantity || 0) || 0;
                next[idx] = {
                  ...next[idx],
                  quantity: String(Math.max(0.01, currentQty + 1)),
                };
                return next;
              });
              if (!isView) setFocusQtyIndex(idx);
              setDuplicateDialog({ open: false, existingIndex: -1, product: null });
              setProductSearch('');
              setPendingBarcode('');
              setScanOpen(false);
            }}
          >
            Yes, increase
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel Entry?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel? Any unsaved changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setCancelDialogOpen(false)}>
            No
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setCancelDialogOpen(false);
              window.history.back();
              setTimeout(() => navigate('/purchases'), 100);
            }}
          >
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete purchase order?</DialogTitle>
        <DialogContent>
          <Typography>
            This will remove the purchase order and reverse stock and accounting entries. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              if (!id) return;
              try {
                await deletePurchaseOrder(id).unwrap();
                setDeleteDialogOpen(false);
                navigate('/purchases');
              } catch (err) {
                alert(err?.data?.message || 'Failed to delete purchase order');
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* IMEI Entry Modal */}
      <Dialog open={imeiModal.open} onClose={() => setImeiModal({ open: false, itemIndex: -1, imeis: [] })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Scan / Enter IMEIs</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Please enter one IMEI per line. You can also use a barcode scanner to scan them sequentially.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="IMEIs / Serial Numbers"
              placeholder="e.g. 123456789012345&#10;123456789012346"
              value={imeiModal.text}
              onChange={(e) => {
                const val = e.target.value;
                const arr = val.split(/[\n,]+/).map((i) => i.trim()).filter((i) => i);
                setImeiModal((prev) => ({ ...prev, text: val, imeis: arr }));
              }}
            />
            <Typography
              variant="caption"
              sx={{ mt: 1, display: 'block', fontWeight: 600 }}
              color={
                imeiModal.itemIndex >= 0 &&
                imeiModal.imeis.length === (parseFloat(items[imeiModal.itemIndex]?.quantity || 0) * (items[imeiModal.itemIndex]?.requiresDualImei ? 2 : 1))
                  ? 'success.main'
                  : 'error.main'
              }
            >
              {imeiModal.imeis.length} of {imeiModal.itemIndex >= 0 ? parseFloat(items[imeiModal.itemIndex]?.quantity || 0) * (items[imeiModal.itemIndex]?.requiresDualImei ? 2 : 1) : 0} required IMEIs provided
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImeiModal({ open: false, itemIndex: -1, imeis: [], text: '' })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const reqQty = (parseFloat(items[imeiModal.itemIndex]?.quantity) || 0) * (items[imeiModal.itemIndex]?.requiresDualImei ? 2 : 1);
              if (imeiModal.imeis.length !== reqQty) {
                alert(`Please provide exactly ${reqQty} IMEIs.`);
                return;
              }
              handleItemChange(imeiModal.itemIndex, 'imeis', imeiModal.imeis);
              setImeiModal({ open: false, itemIndex: -1, imeis: [], text: '' });
            }}
          >
            Save IMEIs
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Form Modal */}
      <Dialog
        open={productDialog.open}
        onClose={() => setProductDialog({ open: false, productId: null })}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 12px 40px rgba(0,0,0,0.15)' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {productDialog.productId ? 'Edit Product' : 'Add Product'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 0, overflow: 'visible', '& .MuiInputLabel-root': { overflow: 'visible' } }}>
          <ProductFormContent
            compact
            productId={productDialog.productId}
            onCancel={() => setProductDialog({ open: false, productId: null })}
            onSuccess={(created) => {
              setProductDialog({ open: false, productId: null });
              if (created?.id) {
                if (productDialog.productId) {
                  setItems((prev) =>
                    prev.map((it) => {
                      if (it.productId === created.id) {
                        return {
                          ...it,
                          productName: created.name,
                          productSku: created.sku,
                          productBrand: created.brand,
                          barcode: created.barcode || it.barcode,
                          unitPrice: created.costPrice != null && created.costPrice !== '' ? Number(created.costPrice) : it.unitPrice,
                          sellingPrice: created.sellingPrice != null && created.sellingPrice !== '' ? Number(created.sellingPrice) : it.sellingPrice,
                          wholesalePrice: created.wholesalePrice != null && created.wholesalePrice !== '' ? Number(created.wholesalePrice) : it.wholesalePrice,
                        };
                      }
                      return it;
                    })
                  );
                } else {
                  handleAddProductRow(created);
                }
              }
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Print Barcodes Modal */}
      <Dialog open={barcodeDialogOpen} onClose={() => setBarcodeDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Print Barcodes</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }} variant="body2" color="text.secondary">
            Select the items and adjust the quantities you want to print barcodes for.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={barcodeSelection.length > 0 && barcodeSelection.every((i) => i.selected)}
                      indeterminate={barcodeSelection.some((i) => i.selected) && !barcodeSelection.every((i) => i.selected)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setBarcodeSelection((prev) => prev.map((i) => ({ ...i, selected: checked })));
                      }}
                    />
                  </TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell width="120">Print Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {barcodeSelection.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={item.selected}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setBarcodeSelection((prev) => {
                            const next = [...prev];
                            next[idx].selected = checked;
                            return next;
                          });
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.productName}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.productSku || item.barcode}</Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={item.printQty}
                        onChange={(e) => {
                          const val = e.target.value;
                          setBarcodeSelection((prev) => {
                            const next = [...prev];
                            next[idx].printQty = val;
                            return next;
                          });
                        }}
                        disabled={!item.selected}
                        inputProps={{ min: 1, step: 1 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBarcodeDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!barcodeSelection.some((i) => i.selected)}
            onClick={() => {
              const selectedItemsToPrint = barcodeSelection
                .filter((i) => i.selected && Number(i.printQty) > 0)
                .map((i) => ({
                  id: i.productId,
                  name: i.productName,
                  sku: i.productSku,
                  barcode: i.barcode,
                  brand: i.productBrand,
                  costPrice: i.unitPrice,
                  sellingPrice: i.sellingPrice,
                  qty: Number(i.printQty) || 1,
                }));

              setBarcodeDialogOpen(false);
              navigate('/products/barcode/print', { state: { products: selectedItemsToPrint } });
            }}
          >
            Print Selected
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PurchaseOrderForm;
